import React from 'react';
import { Card, Menu, Dropdown, Typography, Button, MenuProps, Divider, Image, Modal, Tag, Spin } from 'antd';
import { DeleteOutlined, DownloadOutlined, EditOutlined, EllipsisOutlined, ExportOutlined, LinkOutlined, SelectOutlined } from '@ant-design/icons';
import styled from '@emotion/styled'
import { IFile } from 'interfaces';
import PdfIcon from '../../assets/pdf-icon.png';
import { useDelete, useNavigation } from '@refinedev/core';
import { FILE_RES_ID } from 'utility/constants';
import NoImage from '../../assets/no-image.png';
import useHttpClient from 'components/hooks/useHttpClient';
const { Text } = Typography;

interface FileProductUsage {
  id: number;
  sku: string;
  name: string;
  role: string;
  thumb: string;
}

const MediaCardWrapper = styled('div')`
    box-shadow: 0 4px 8px 0 rgba(0,0,0,0.2);
    transition: 0.3s;
    border-radius: 8px;
    
    img {
        object-fit: contain !important;
        height: 110px !important;
        cursor: pointer;
    }

    .ant-image {
      width: 100%;
      border-bottom: 1px solid #Fefe;
      padding-bottom: 0.5rem;
    }

    .media-card-body {
      padding: 2px 4px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .media-card-body .ant-btn-icon {
      transform: rotate(90deg);
    }

    .media-card-body .ant-typography {
      font-weight: 500;
      overflow: hidden;
      font-size: 12px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      padding: 0px 8px;
    }

    &:hover {
        box-shadow: 0 8px 16px 0 rgba(0,0,0,0.2);
    }
`

const MenuDivider = styled(Divider)`
    margin: 4px 0px !important;
`

interface RelationCardProps {
  file: IFile;
}

const MediaCard: React.FC<RelationCardProps> = ({ file }) => {
  const { mutate } = useDelete();
  const { makeHttpRequest } = useHttpClient();
  const { push } = useNavigation();
  const { id, name, url, previewUrl, extension } = file;
  
  function openInNewTab(url : string) {
    const win = window.open(url, '_blank');
    win?.focus();
  }

  const handleShowLinkedProducts = async () => {
    const modal = Modal.info({
      title: "Laddar...",
      width: 420,
      okText: "Stäng",
      okType: "primary",
      okButtonProps: {
        style: {
          backgroundColor: "#195178",
          borderColor: "#195178",
        },
      },
      icon: null,
      content: (
        <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
          <Spin />
        </div>
      ),
    });

    const result = await makeHttpRequest("GET", `/files/${id}/products`);

    if (result?.errorObject) {
      modal.update({
        title: "Kopplade produkter",
        content: <Text type="danger">Kunde inte hämta kopplade produkter.</Text>,
      });
      return;
    }

    const products = (result as FileProductUsage[]) ?? [];

    modal.update({
      title: `Används av ${products.length} produkt${products.length === 1 ? "" : "er"}`,
      content:
        products.length === 0 ? (
          <Text type="secondary" style={{ padding: "8px 16px", display: "block" }}>
            Filen används inte av någon produkt.
          </Text>
        ) : (
          <div style={{ margin: "0 -24px", maxHeight: 360, overflowY: "auto" }}>
            {products.map((r) => (
              <div
                key={`${r.id}-${r.sku}-${r.role}`}
                onClick={() => {
                  modal.destroy();
                  push(`/products/edit/media/${r.id}`);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 16px",
                  borderTop: "1px solid #f5f5f5",
                  cursor: "pointer",
                }}
              >
                <img
                  src={r.thumb || NoImage}
                  alt=""
                  width={28}
                  height={28}
                  style={{
                    borderRadius: 4,
                    objectFit: "cover",
                    background: "#f0f2f5",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text ellipsis style={{ fontSize: 13, display: "block" }}>
                    {r.name || "—"}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {r.sku}
                  </Text>
                </div>
                <Tag
                  color={r.role === "Huvudbild" ? "geekblue" : "default"}
                  style={{ marginInlineEnd: 0 }}
                >
                  {r.role}
                </Tag>
              </div>
            ))}
          </div>
        ),
    });
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Bekräfta borttagning",
      content: (
        <>
          Är du säker på att du vill ta bort filen "{name}"?
          <br />
          Om filen används på produkter tas den bort från alla kopplade produkter,
          och integrationer körs som vid en vanlig produktändring.
        </>
      ),
      okText: "Ja, ta bort",
      cancelText: "Avbryt",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          mutate(
            {
              resource: FILE_RES_ID,
              id: id,
            },
            {
              onSuccess: () => resolve(),
              onError: () => reject(),
            }
          );
        }),
    });
  };

  const menu = (
    <Menu>
      {/* <Menu.Item
        key="edit"
        onClick={() => console.log(id)}
        icon={<EditOutlined></EditOutlined>}
      >
        Redigera
      </Menu.Item> */}
      <Menu.Item
        key="open-new-tab"
        onClick={() => openInNewTab(url)}
        icon={<SelectOutlined></SelectOutlined>}
      >
        Öppna i ny tabb
      </Menu.Item>
      <Menu.Item
        key="download"
        onClick={() => downloadFile(url, name)}
        icon={<DownloadOutlined></DownloadOutlined>}
      >
        Ladda ner
      </Menu.Item>
      <Menu.Item
        key="linked-products"
        onClick={handleShowLinkedProducts}
        icon={<LinkOutlined />}
      >
        Visa kopplade produkter
      </Menu.Item>
      <MenuDivider></MenuDivider>
      <Menu.Item
        key="delete"
        onClick={handleDelete}
        icon={<DeleteOutlined></DeleteOutlined>}
      >
        Ta bort
      </Menu.Item>
    </Menu>
  );


  function downloadFile(url: string, fileName: string) {
    fetch(url, {
      method: "GET",
      headers: {
        "Cache-Control": "no-cache", // Prevent caching
        Pragma: "no-cache", // For HTTP/1.0 compatibility
      },
    })
      .then((res) => {
        return res.blob();
      })
      .then((blob) => {
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout((_) => {
          window.URL.revokeObjectURL(url);
        }, 60000);
        a.remove();
      })
      .catch((err) => {
        console.error("err: ", err);
      });
  }

  return (
    <MediaCardWrapper>
      {file.extension === ".pdf" ? (
        <Image
          src={PdfIcon}
          alt={name}
          preview={{
            imageRender: () => (
              <iframe
                src={url}
                style={{ width: "70%", height: "100%" }}
                frameBorder="0"
              ></iframe>
            ),
            toolbarRender: () => null,
          }}
        />
      ) : (
        <Image src={previewUrl} fallback={NoImage}/>
      )}
      <div className="media-card-body">
        <Text title={name}>{name}</Text>
        <Dropdown overlay={menu} trigger={["click"]}>
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      </div>
    </MediaCardWrapper>
  );
};

export default MediaCard;
